import { dayKey, formatTime, londonWallClockIso } from "../format.js";
import { availabilityDateOffset } from "./availabilityRange.js";
import type { RecurrenceCadence } from "./recurrences.js";
import { staffWorkingHours, type StaffSlotCheck } from "./staffWorkingHours.js";

export type RecurrencePreflightStatus =
  | "available"
  | "unavailable"
  | "no_credit"
  | "refused";

export interface RecurrencePreflightDecision {
  status: RecurrencePreflightStatus;
  funding: "credit" | "payment_pending" | null;
  message: string;
}

export interface RecurrenceFundingOfferingSnapshot {
  available_credits: number;
  book_with_credit: {
    allowed: boolean;
    reasons: readonly { message: string }[];
  };
  request_onsite: {
    allowed: boolean;
    reasons: readonly { message: string }[];
  };
}

export interface RecurrencePreviewOccurrence {
  startsAt: string;
  state: StaffSlotCheck["state"];
  conflict: boolean;
  warning: boolean;
  message: string | null;
  preflight: RecurrencePreflightDecision | null;
}

export interface RecurrencePreview {
  occurrences: RecurrencePreviewOccurrence[];
  conflictCount: number;
  warningCount: number;
}

/**
 * Mirror WMF-SIte's recurrence cadence using London calendar weeks rather than
 * elapsed milliseconds. The first slot is an absolute instant, so derive its
 * London date/time before stepping calendar days. Converting every later wall
 * clock back to an instant keeps (for example) a 16:00 lesson at 16:00 across
 * the BST/GMT boundary.
 */
export function recurringLocalStarts(
  startsAt: string,
  cadence: RecurrenceCadence,
  occurrenceCount: number,
): string[] {
  const firstDay = dayKey(startsAt);
  const displayedTime = formatTime(startsAt);
  const localTime = displayedTime === "24:00" ? "00:00" : displayedTime;
  const intervalDays = cadence === "weekly" ? 7 : 14;

  return Array.from({ length: occurrenceCount }, (_, index) => {
    const day = availabilityDateOffset(firstDay, intervalDays * index);
    return londonWallClockIso(day, localTime);
  });
}

function conflictMessage(check: StaffSlotCheck): string | null {
  if (check.state === "available") return null;
  if (check.message) return check.message;
  if (check.state === "outside_working_hours") {
    return "Outside the tutor's normal teaching hours.";
  }
  return "This lesson time is not available.";
}

export function recurringOutsideHoursWarning(check: StaffSlotCheck): boolean {
  return check.state === "outside_working_hours" && check.working_hours_override_available;
}

/**
 * Check every occurrence without creating appointments. The exact-slot read is
 * owned by WMF-SIte and distinguishes a real conflict from an otherwise-free
 * time outside the tutor's configured working hours. The latter is a warning,
 * not a hard stop, when WMF-SIte explicitly says a working-hours override is
 * available; recurring creation may proceed and the backend remains
 * authoritative for the final per-occurrence outcome.
 *
 * Limit concurrency so a long (up to 52 occurrence) preview does not fan out
 * dozens of requests at once.
 */
export async function previewRecurringLessonOccurrences(input: {
  providerId: number;
  serviceId: number;
  startsAt: string;
  cadence: RecurrenceCadence;
  occurrenceCount: number;
}): Promise<RecurrencePreview> {
  const starts = recurringLocalStarts(input.startsAt, input.cadence, input.occurrenceCount);
  const occurrences: RecurrencePreviewOccurrence[] = [];
  const concurrency = 6;

  for (let offset = 0; offset < starts.length; offset += concurrency) {
    const batch = starts.slice(offset, offset + concurrency);
    const checks = await Promise.all(
      batch.map((startsAt) =>
        staffWorkingHours.check({
          providerId: input.providerId,
          serviceId: input.serviceId,
          startsAt,
        }),
      ),
    );
    occurrences.push(
      ...checks.map((check, index) => {
        const warning = recurringOutsideHoursWarning(check);
        return {
          startsAt: batch[index]!,
          state: check.state,
          conflict: check.state !== "available" && !warning,
          warning,
          message: conflictMessage(check),
          preflight: null,
        };
      }),
    );
  }

  return {
    occurrences,
    conflictCount: occurrences.filter((occurrence) => occurrence.conflict).length,
    warningCount: occurrences.filter((occurrence) => occurrence.warning).length,
  };
}

function firstReason(
  reasons: readonly { message: string }[],
): string | null {
  return reasons.find((reason) => reason.message.trim() !== "")?.message ?? null;
}

/**
 * Add an advisory, authoritative-snapshot funding decision to each occurrence.
 * Credits are reported in date order from the server's current offering balance;
 * no credit is reserved here. FastAPI remains authoritative at the write.
 */
export function applyRecurrenceFundingPreview(
  preview: RecurrencePreview,
  offering: RecurrenceFundingOfferingSnapshot | null,
): RecurrencePreview {
  const reportedCredits =
    offering !== null && Number.isFinite(offering.available_credits)
      ? Math.max(0, Math.floor(offering.available_credits))
      : 0;
  let creditsRemaining =
    offering?.book_with_credit.allowed === true ? reportedCredits : 0;

  const occurrences = preview.occurrences.map((occurrence) => {
    if (occurrence.conflict) {
      return {
        ...occurrence,
        preflight: {
          status: "unavailable" as const,
          funding: null,
          message:
            occurrence.message ??
            "The booking system reports this lesson as unavailable.",
        },
      };
    }

    if (offering === null) {
      return {
        ...occurrence,
        preflight: {
          status: "refused" as const,
          funding: null,
          message: "A current tutor, service and funding option could not be confirmed.",
        },
      };
    }

    if (reportedCredits > 0 && !offering.book_with_credit.allowed) {
      return {
        ...occurrence,
        preflight: {
          status: "refused" as const,
          funding: null,
          message:
            firstReason(offering.book_with_credit.reasons) ??
            "The booking system refused credit-funded booking for this lesson.",
        },
      };
    }

    if (creditsRemaining > 0) {
      creditsRemaining -= 1;
      return {
        ...occurrence,
        preflight: {
          status: "available" as const,
          funding: "credit" as const,
          message: "A usable credit is available at preview time.",
        },
      };
    }

    if (offering.request_onsite.allowed) {
      return {
        ...occurrence,
        preflight: {
          status: "no_credit" as const,
          funding: "payment_pending" as const,
          message:
            "No usable credit is available at preview time; payment-pending booking is allowed.",
        },
      };
    }

    return {
      ...occurrence,
      preflight: {
        status: "refused" as const,
        funding: null,
        message:
          firstReason(offering.request_onsite.reasons) ??
          firstReason(offering.book_with_credit.reasons) ??
          "No eligible credit or payment-pending route is available.",
      },
    };
  });

  return { ...preview, occurrences };
}
